import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { queryKeys } from '@/query/keys';

import type {
  ConsentTypeActiveFilter,
  ConsentTypeDefDto,
  JsonPatchOp,
} from './endpoints';

/**
 * View model normalises nullable BE fields so the UI doesn't branch on
 * undefined. Sort order is nullable in the wire DTO but we surface it as a
 * number (0 when unset) since the admin table always sorts by it.
 */
export interface AdminConsentTypeViewModel {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly sortOrder: number;
  readonly isRequired: boolean;
  readonly isActive: boolean;
}

export function mapAdminConsentType(dto: ConsentTypeDefDto): AdminConsentTypeViewModel {
  return {
    id: dto.id ?? '',
    name: dto.name,
    description: dto.description ?? '',
    sortOrder: dto.sort_order ?? 0,
    isRequired: dto.is_required ?? false,
    isActive: dto.is_active ?? true,
  };
}

export interface AdminConsentTypeInput {
  readonly name: string;
  readonly description: string;
  readonly sortOrder: number;
  readonly isRequired: boolean;
  readonly isActive: boolean;
}

export interface UpdateAdminConsentTypeInput extends AdminConsentTypeInput {
  readonly id: string;
  /**
   * Fields the current mutation should touch. Anything not listed is left as-is
   * on the server, keeping the PATCH surgical (a JSON-Patch op per changed field).
   */
  readonly changed: readonly (keyof AdminConsentTypeInput)[];
}

function toCreateBody(input: AdminConsentTypeInput): ConsentTypeDefDto {
  return {
    name: input.name,
    description: input.description.length > 0 ? input.description : null,
    sort_order: input.sortOrder,
    is_required: input.isRequired,
    is_active: input.isActive,
  };
}

function toPatchOps(input: UpdateAdminConsentTypeInput): JsonPatchOp[] {
  const ops: JsonPatchOp[] = [];
  for (const field of input.changed) {
    switch (field) {
      case 'name':
        ops.push({ op: 'replace', path: '/name', value: input.name });
        break;
      case 'description':
        ops.push({
          op: 'replace',
          path: '/description',
          value: input.description.length > 0 ? input.description : null,
        });
        break;
      case 'sortOrder':
        ops.push({ op: 'replace', path: '/sort_order', value: input.sortOrder });
        break;
      case 'isRequired':
        ops.push({ op: 'replace', path: '/is_required', value: input.isRequired });
        break;
      case 'isActive':
        ops.push({ op: 'replace', path: '/is_active', value: input.isActive });
        break;
    }
  }
  return ops;
}

export const adminConsentTypesQueryKey = queryKeys.consentTypes;

export function useAdminConsentTypes(
  active: ConsentTypeActiveFilter = 'all',
): UseQueryResult<AdminConsentTypeViewModel[]> {
  return useQuery<AdminConsentTypeViewModel[]>({
    queryKey: [...adminConsentTypesQueryKey, active] as const,
    queryFn: async ({ signal }) => {
      const dtos = await apiClient.listConsentTypes({ active, signal });
      return dtos.map(mapAdminConsentType);
    },
  });
}

type Snapshot = AdminConsentTypeViewModel[] | undefined;

export function useAdminConsentTypeMutations(): {
  readonly create: UseMutationResult<AdminConsentTypeViewModel, Error, AdminConsentTypeInput>;
  readonly update: UseMutationResult<
    AdminConsentTypeViewModel,
    Error,
    UpdateAdminConsentTypeInput,
    { previous: Snapshot }
  >;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    queryClient
      .invalidateQueries({ queryKey: adminConsentTypesQueryKey })
      .catch(() => undefined);
  };

  const create = useMutation<AdminConsentTypeViewModel, Error, AdminConsentTypeInput>({
    mutationFn: async (input) =>
      mapAdminConsentType(await apiClient.createConsentType(toCreateBody(input))),
    onSettled: invalidate,
  });

  const update = useMutation<
    AdminConsentTypeViewModel,
    Error,
    UpdateAdminConsentTypeInput,
    { previous: Snapshot }
  >({
    mutationFn: async (input) => {
      const ops = toPatchOps(input);
      if (ops.length === 0) {
        // Nothing to send — just re-map local state to the view model shape.
        return {
          id: input.id,
          name: input.name,
          description: input.description,
          sortOrder: input.sortOrder,
          isRequired: input.isRequired,
          isActive: input.isActive,
        };
      }
      return mapAdminConsentType(await apiClient.patchConsentType(input.id, ops));
    },
    onSettled: invalidate,
  });

  return { create, update };
}
